import { getCourseResources } from 'core/crud/courses.crud';
import { getLessons } from 'core/crud/lessons.crud';
import { useOrganization } from 'core/hooks/useOrganization';
import * as coreEntities from 'core/store/index';
import { COURSE_TYPES } from 'core/utils/constants/course';
import { successResponse } from 'core/utils/utils';
import { CLIENT, CONTENT_TYPES, LESSON_TYPES_IDS } from 'core/variables/constant';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

/**
 * Custom hook for managing quiz location selection dialog state and logic
 * @returns {Object} State and handlers for the quiz location dialog
 */
function useQuizLocationDialog() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { client } = useOrganization();

  // Detect Edelvives tenant - uses different flow (toolbox/resources page)
  const isEdelvives = client === CLIENT.EDELVIVES;

  // Selection state
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  // Data state
  const [units, setUnits] = useState([]);

  // Edelvives: Test sections for deep link
  const [testSections, setTestSections] = useState([]);
  const [selectedTestGroup, setSelectedTestGroup] = useState(null);

  // Loading state
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingTestSections, setLoadingTestSections] = useState(false);

  // Get courses from Redux store
  const courses = useSelector((state) => coreEntities.courses.selectors.getCourses(state)) || [];

  // Load courses on mount
  useEffect(() => {
    dispatch(coreEntities.courses.actions.getCourses({ params: { offset: 0, pageSize: 1000 } }));
  }, [dispatch]);

  // Derived lessons from selected unit (filter out MINT - Edelvives uses different flow)
  const lessons = useMemo(() => {
    if (!selectedUnit?.data) return [];
    const unitLessons = selectedUnit.data.items || [];
    return unitLessons.filter((lesson) => lesson.lesson_type !== LESSON_TYPES_IDS.MINT);
  }, [selectedUnit]);

  // Load units when course is selected
  const loadCourseUnits = useCallback(async (courseGuid) => {
    setLoadingUnits(true);
    setUnits([]);

    try {
      const response = await getLessons({ guid: courseGuid });
      if (successResponse(response) && response.data?.data?.items) {
        const courseUnits = response.data.data.items || [];
        setUnits(courseUnits);
      }
    } catch (error) {
      console.error('[QuizLocationDialog] Error loading units:', error);
      setUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  }, []);

  // Helper to get the correct GUID for resources API (Edelvives)
  const getGuidForResources = useCallback((course) => {
    if (course.type === COURSE_TYPES.TEMPLATE_1) return course.guid;
    if (course.type === COURSE_TYPES.TEMPLATE_2 || course.type === COURSE_TYPES.NO_REFERENCED) {
      return course.course_template_guid;
    }
    if (course.type === COURSE_TYPES.REFERENCED) return course.program_guid;
    return course.guid;
  }, []);

  // Load test sections for Edelvives deep link
  const loadTestSections = useCallback(
    async (course) => {
      if (!isEdelvives) return;

      setLoadingTestSections(true);
      setTestSections([]);
      setSelectedTestGroup(null);

      try {
        const courseGuid = getGuidForResources(course);
        const referencedCourseGuid = course.type === COURSE_TYPES.REFERENCED ? course.guid : null;

        const response = await getCourseResources({ courseGuid, referencedCourseGuid });
        const sections = response.data?.data?.items || [];

        // Find all test sections and flatten groups
        const testGroups = [];
        sections.forEach((section) => {
          if (section.type === 'test') {
            const sectionItems = section.items || [];
            sectionItems.forEach((group) => {
              testGroups.push({
                value: `${section.guid}|${group.guid}`,
                label: group.name || section.name,
                sectionGuid: section.guid,
                groupGuid: group.guid,
                data: { section, group },
              });
            });
          }
        });

        setTestSections(testGroups);

        // Auto-select if only one group
        if (testGroups.length === 1) {
          setSelectedTestGroup(testGroups[0]);
        }
      } catch (error) {
        console.error('[QuizLocationDialog] Error loading test sections:', error);
        setTestSections([]);
      } finally {
        setLoadingTestSections(false);
      }
    },
    [isEdelvives, getGuidForResources]
  );

  // Handlers
  const handleCourseChange = useCallback(
    (option) => {
      setSelectedCourse(option);
      setSelectedUnit(null);
      setSelectedLesson(null);
      setUnits([]);
      setTestSections([]);
      setSelectedTestGroup(null);

      if (option?.value) {
        if (isEdelvives && option?.data) {
          loadTestSections(option.data);
        } else {
          loadCourseUnits(option.value);
        }
      }
    },
    [loadCourseUnits, loadTestSections, isEdelvives]
  );

  const handleTestGroupChange = useCallback((option) => {
    setSelectedTestGroup(option);
  }, []);

  const handleUnitChange = useCallback((option) => {
    setSelectedUnit(option);
    setSelectedLesson(null);
  }, []);

  const handleLessonChange = useCallback((option) => {
    setSelectedLesson(option);
  }, []);

  const resetSelections = useCallback(() => {
    setSelectedCourse(null);
    setSelectedUnit(null);
    setSelectedLesson(null);
    setUnits([]);
    setTestSections([]);
    setSelectedTestGroup(null);
  }, []);

  // Build navigation URL to quiz creation page
  const navigationUrl = useMemo(() => {
    if (!selectedCourse) return null;

    const courseGuid = selectedCourse.value;

    // Edelvives: Deep link to create-evaluations page
    if (isEdelvives) {
      // If we have a selected test group, deep link to create-evaluations
      if (selectedTestGroup) {
        return `/course/${courseGuid}/create-evaluations/${selectedTestGroup.sectionGuid}/${selectedTestGroup.groupGuid}`;
      }
      // No test sections found - return null (error will be shown)
      return null;
    }

    // Other tenants: Require lesson selection
    if (!selectedLesson) return null;

    const lessonGuid = selectedLesson.value;
    const lessonType = selectedLesson.data?.lesson_type;
    const isPowerCourse = selectedCourse.data?.has_power_lesson === 1;

    // For power lessons, navigate to lesson page with addQuiz param
    if (lessonType === LESSON_TYPES_IDS.POWER || isPowerCourse) {
      return `/course/${courseGuid}/lesson/${lessonGuid}?addQuiz=true&enableAI=true`;
    }

    // For regular lessons, navigate to quiz creation page
    return `/course/${courseGuid}/lesson/${lessonGuid}/add/${CONTENT_TYPES.TEST}?enableAI=true`;
  }, [selectedCourse, selectedLesson, selectedTestGroup, isEdelvives]);

  // Edelvives: Can navigate if course selected AND test section available
  // Other tenants: Need both course and lesson selected
  const canNavigate = useMemo(() => {
    if (isEdelvives) {
      if (!selectedCourse) return false;
      if (loadingTestSections) return false;
      // Require at least one test section to navigate
      if (testSections.length === 0) return false;
      // If there are multiple test sections, require selection
      if (testSections.length > 1) return Boolean(selectedTestGroup);
      // If only one test section (auto-selected), can navigate
      return true;
    }
    return Boolean(selectedCourse && selectedLesson);
  }, [isEdelvives, selectedCourse, selectedLesson, testSections, selectedTestGroup, loadingTestSections]);

  const handleNavigate = useCallback(() => {
    if (navigationUrl) {
      history.push(navigationUrl);
    }
  }, [history, navigationUrl]);

  return {
    // Data
    courses,
    units,
    lessons,

    // Edelvives test sections
    testSections,

    // Selected values
    selectedCourse,
    selectedUnit,
    selectedLesson,
    selectedTestGroup,

    // Loading states
    loadingUnits,
    loadingTestSections,

    // Handlers
    handleCourseChange,
    handleUnitChange,
    handleLessonChange,
    handleTestGroupChange,
    handleNavigate,
    resetSelections,

    // Computed
    canNavigate,
    navigationUrl,

    // Tenant flags
    isEdelvives,
  };
}

export default useQuizLocationDialog;
